import React from 'react';
import Drawer, { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import DrawerHandler from '@app/component/overlays/drawer/DrawerHandler';
import { useQueries } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import Utilities from '@util/Utilities';
import PriceSubmissionLogTableFragment from './fragment/PriceSubmissionLogTableFragment';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';

type PriceSubmissionLogsForStatDateDrawerProps = {
	statDate: string,
} & Omit<DrawerProps, 'title' | 'ref'>;

const PriceSubmissionsForStatDateDrawer = React.memo(
	React.forwardRef(function PriceSubmissionLogsForStatDateDrawer({
																																	 statDate,
																																	 ...props
																																 }: PriceSubmissionLogsForStatDateDrawerProps, ref: React.Ref<DrawerHandler>) {

		const { companyHotel } = useSelectedCompanyHotel();
		const [selectedSubmissionId, setSelectedSubmissionId] = React.useState<number | undefined>(undefined);

		const results = useQueries({
			queries: [
				{
					queryKey: PricingAutomationQueryKeys.pricingRooms(companyHotel.id),
					queryFn: () => PricingAutomationService.findCompanyHotelPricingRooms(companyHotel.id),
					enabled: !!companyHotel?.id,
				}, {
					queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
					queryFn: () =>
						PricingAutomationService.findCompanyHotelPricingArchitectures(companyHotel.id),
					enabled: !!companyHotel?.id,
				}, {
					queryKey: PricingAutomationQueryKeys.pricingSubmissionsForStatDate(companyHotel.id, statDate),
					queryFn: () => PricingAutomationService.findCompanyHotelPricingSubmissionsForStatDate(companyHotel.id, statDate),
					enabled: !!companyHotel?.id,
				}],
		});

		const isLoading = results.some(result => result.isLoading);
		const isError = results.some(result => result.isError);
		const pricingRooms = results[0].data ?? [];
		const architectures = results[1].data ?? [];
		const submissions = React.useMemo(() => {
			return (results[2].data ?? [])
				.map(submission => ({
					submission,
					items: submission.items.filter(item => item.statDate === statDate),
				}))
				.filter(entry => entry.items.length > 0)
				.sort((a, b) => getSubmissionDisplayDate(b.submission).localeCompare(getSubmissionDisplayDate(a.submission)));
		}, [results[2].data, statDate]);
		const selectedSubmission =
			submissions.find(entry => entry.submission.id === selectedSubmissionId) ?? submissions[0];
		const recommendationStateIds = React.useMemo(() => {
			return [...new Set((selectedSubmission?.items ?? []).map(item => item.recommendationStateId))];
		}, [selectedSubmission]);
		const recommendationStateResults = useQueries({
			queries: recommendationStateIds.map(recommendationStateId => ({
				queryKey: MarketStateGraphQLQueryKeys.priceRecommendationState(
					companyHotel.id,
					recommendationStateId
				),
				queryFn: () =>
					MarketStateService.findCompanyHotelPriceRecommendationState(
						companyHotel.id,
						recommendationStateId
					),
				enabled: !!companyHotel?.id && selectedSubmission !== undefined && !!recommendationStateId,
			})),
		});
		const isLoadingRecommendationStates = recommendationStateResults.some(result => result.isLoading);
		const isErrorRecommendationStates = recommendationStateResults.some(result => result.isError);
		const recommendationStates = recommendationStateResults
			.map(result => result.data)
			.filter((state): state is PriceRecommendationState => state !== null && state !== undefined);

		React.useEffect(() => {
			if (!_isEmpty(submissions) && selectedSubmissionId === undefined) {
				setSelectedSubmissionId(submissions[0].submission.id);
			}
		}, [submissions, selectedSubmissionId]);

		return <Drawer title={`Price Submission Log for ${Utilities.formatDateString(statDate, 'DD/MM/yyyy', false)}`}
		               ref={ref}
		               size="large"
									 zIndexClass="z-[100000]"
		               isLoading={isLoading}
		               {...props}>
			<div className="mt-6">
				<div className="px-4 sm:px-6 mt-5">
					{isLoading && <TablePlaceholder />}
					{isError && <Message state="error">Error loading price submission logs.</Message>}
					{!isLoading && !isError && _isEmpty(submissions) &&
						<Message state="info">No price submission logs found for the selected date.</Message>}
					{!isLoading && !isError && !_isEmpty(submissions) && selectedSubmission !== undefined && <div className="space-y-4">
						<div className="space-y-2">
							<div className="flex items-center gap-x-2 sm:max-w-lg">
								<label
									className="whitespace-nowrap text-sm font-medium text-gray-700"
									htmlFor="price-submission-submitted-date"
								>
									Submitted Date
								</label>
								<select
									id="price-submission-submitted-date"
									aria-label="Select submitted date"
									value={selectedSubmission.submission.id}
									onChange={e => setSelectedSubmissionId(Number(e.target.value))}
									className="w-full input"
								>
									{submissions.map(({ submission }) => (
										<option key={submission.id} value={submission.id}>
											{`${formatSubmissionDate(submission)} · ${submission.getSubmitterName()}`}
										</option>
									))}
								</select>
							</div>
							<p className="text-sm text-gray-700">
								<span className="font-medium">Submitted by:</span> {selectedSubmission.submission.getSubmitterName()}
							</p>
						</div>
						{isLoadingRecommendationStates && <TablePlaceholder />}
						{isErrorRecommendationStates && (
							<Message state="error">Error loading price recommendation state.</Message>
						)}
						{!isLoadingRecommendationStates && !isErrorRecommendationStates && (
							<PriceSubmissionLogTableFragment
								companyHotel={companyHotel}
								pricingRooms={pricingRooms}
								architectures={architectures}
								statDate={statDate}
								items={selectedSubmission.items}
								recommendationStates={recommendationStates}
							/>
						)}
					</div>}
				</div>
			</div>
		</Drawer>;
	}),
);

const getSubmissionDisplayDate = (submission: { submissionDate?: string | null, finishDate?: string | null, creationDate?: string | null }) => {
	return submission.submissionDate ?? submission.finishDate ?? submission.creationDate ?? '';
};

const formatSubmissionDate = (submission: { submissionDate?: string | null, finishDate?: string | null, creationDate?: string | null }) => {
	const displayDate = getSubmissionDisplayDate(submission);
	return displayDate ? Utilities.formatDateString(displayDate, 'DD/MM/yyyy HH:mm:ss') : '-';
};

export default PriceSubmissionsForStatDateDrawer;
